import { trigger, transition, style, query, animate } from '@angular/animations';
import * as i0 from '@angular/core';
import { Injectable, EventEmitter, inject, ChangeDetectorRef, Component, ChangeDetectionStrategy, Input, HostBinding, Output, ViewChild, ElementRef, ContentChildren, NgModule } from '@angular/core';
import { areAnimationsDisabled, WebComponentContentChildren } from '@simpl/element-ng/common';
import { ReplaySubject, Subject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i2 from '@simpl/element-ng/translate';
import { SiTranslateModule } from '@simpl/element-ng/translate';
import { __decorate } from 'tslib';
import { ResizeObserverService } from '@simpl/element-ng/resize-observer';

/** @internal */
class SiAccordionHCollapseService {
    /**
     * Subject emitting the current horizontal collapsed state.
     */
    hcollapsed$ = new ReplaySubject(1);
    /**
     * Subject to emit to open the accordion.
     */
    open$ = new Subject();
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAccordionHCollapseService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAccordionHCollapseService, providedIn: null });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAccordionHCollapseService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: null
                }]
        }] });

/** @internal */
class SiAccordionService {
    /**
     * Subject to emit an item in the accordion which should be toggled.
     */
    toggle$ = new Subject();
    /**
     * Subject to emit when the items should be expanded to their full height or restored to normal height.
     */
    fullHeight$ = new ReplaySubject(1);
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAccordionService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAccordionService, providedIn: null });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAccordionService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: null
                }]
        }] });

let controlIdCounter = 1;
class SiCollapsiblePanelComponent {
    /**
     * Heading for the collapsible panel.
     */
    heading;
    /**
     * Additional CSS classes for the top element.
     */
    headerCssClasses = '';
    /**
     * Additional CSS classes for the collapsible content region.
     */
    contentBgClasses = '';
    /**
     * Additional CSS classes for the wrapping content element.
     */
    contentCssClasses = '';
    /**
     * Expand/collapse the panel.
     */
    opened = false;
    /**
     * The icon to be displayed besides the heading.
     */
    icon;
    /**
     * Whether the si-collapsible-panel is disabled.
     */
    disabled = false;
    /** Color to use for component background */
    colorVariant;
    /**
     * Defines the content of the optional badge. Should be a number or something like "100+".
     * if undefined or empty string, no badge is displayed
     */
    badge;
    /**
     * Defines the background color of the badge. Default is specific to SiMPL flavour.
     */
    badgeColor;
    /**
     * An event emitted when the user triggered expand/collapse.
     * The event is emitted before the animation happens.
     */
    toggle = new EventEmitter();
    hcollapsed = false;
    fullHeight = false;
    controlId = '__si-collapsible-' + controlIdCounter++;
    headerId = this.controlId + '-header';
    cdRef = inject(ChangeDetectorRef);
    accordionService = inject(SiAccordionService, { optional: true });
    accordionHCollapseService = inject(SiAccordionHCollapseService, { optional: true });
    toggleSubscription = new Subscription();
    enableAnimation = true;
    animationsGloballyDisabled = areAnimationsDisabled();
    lastScrollPos = 0;
    contentRef;
    constructor() {
        if (this.accordionService) {
            this.toggleSubscription.add(this.accordionService.fullHeight$.subscribe(resp => {
                this.fullHeight = resp;
                this.cdRef.markForCheck();
            }));
            this.toggleSubscription.add(this.accordionService.toggle$
                .pipe(filter(item => item !== this))
                .subscribe(() => this.openClose(false)));
        }
        if (this.accordionHCollapseService) {
            this.toggleSubscription.add(this.accordionHCollapseService.hcollapsed$.subscribe(state => {
                this.hcollapsed = state;
                this.cdRef.markForCheck();
            }));
        }
    }
    get showHide() {
        if (this.enableAnimation && !this.animationsGloballyDisabled) {
            return this.opened ? 'show' : 'hide';
        }
        return 'disabled';
    }
    get isHCollapsible() {
        return !!this.accordionHCollapseService;
    }
    ngOnDestroy() {
        this.toggleSubscription?.unsubscribe();
    }
    /**
     * Expand/collapse panel.
     * @param open indicate the panel shall open or close
     * @param enableAnimation with animation
     */
    openClose(open, enableAnimation = true) {
        this.opened = open;
        this.enableAnimation = enableAnimation;
        this.cdRef.markForCheck();
        if (open) {
            setTimeout(() => {
                this.contentRef.nativeElement.scrollTop = this.lastScrollPos;
            });
        }
        else {
            this.lastScrollPos = this.contentRef.nativeElement.scrollTop;
        }
    }
    doToggle(event) {
        if (this.disabled) {
            return;
        }
        event?.preventDefault();
        this.toggle.emit();
        this.openClose(this.hcollapsed || !this.opened);
        this.accordionService?.toggle$.next(this);
        if (this.hcollapsed) {
            this.accordionHCollapseService?.open$.next(this);
        }
    }
    keydown(event) {
        if (event.key === 'Enter' || event.key === 'Space' || event.key === ' ') {
            this.doToggle(undefined);
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiCollapsiblePanelComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.1.5", type: SiCollapsiblePanelComponent, selector: "si-collapsible-panel", inputs: { heading: "heading", headerCssClasses: "headerCssClasses", contentBgClasses: "contentBgClasses", contentCssClasses: "contentCssClasses", opened: "opened", icon: "icon", disabled: "disabled", colorVariant: "colorVariant", badge: "badge", badgeColor: "badgeColor" }, outputs: { toggle: "toggle" }, host: { properties: { "class.opened": "this.opened", "class": "this.colorVariant", "class.hcollapsed": "this.hcollapsed", "class.full-height": "this.fullHeight" } }, viewQueries: [{ propertyName: "contentRef", first: true, predicate: ["content"], descendants: true, static: true }], ngImport: i0, template: "<div\n  class=\"collapsible-header focus-inside px-6\"\n  role=\"button\"\n  [ngClass]=\"headerCssClasses\"\n  [attr.tabindex]=\"disabled ? '' : '0'\"\n  [id]=\"headerId\"\n  [class.open]=\"opened\"\n  [class.disabled]=\"disabled\"\n  [title]=\"hcollapsed ? (heading | translate) : ''\"\n  [attr.aria-expanded]=\"opened && !hcollapsed\"\n  [attr.aria-disabled]=\"disabled\"\n  [attr.aria-controls]=\"controlId\"\n  [attr.aria-label]=\"hcollapsed ? (heading | translate) : ''\"\n  (keydown)=\"keydown($event)\"\n  (click)=\"doToggle($event)\"\n>\n  <i\n    *ngIf=\"icon\"\n    class=\"icon ms-n2 me-2\"\n    aria-hidden=\"true\"\n    [class.collapsed-icon]=\"isHCollapsible\"\n    [ngClass]=\"icon\"\n  ></i>\n  <span *ngIf=\"icon && badge !== undefined && badge !== ''\" class=\"badge-text\">\n    {{ badge }}\n  </span>\n  <span class=\"si-title-2 autohide\">{{ heading | translate }}</span>\n  <span\n    *ngIf=\"badge !== undefined && badge !== ''\"\n    class=\"badge autohide\"\n    [ngClass]=\"'bg-' + badgeColor\"\n  >\n    {{ badge }}\n  </span>\n  <span class=\"ms-auto overflow-hidden\">\n    <span class=\"icon dropdown-caret element-down-2\" aria-hidden=\"true\"></span>\n  </span>\n</div>\n<div\n  #content\n  class=\"collapsible-content\"\n  role=\"region\"\n  [id]=\"controlId\"\n  [ngClass]=\"contentBgClasses\"\n  [@showHide]=\"showHide\"\n  [attr.aria-labelledby]=\"headerId\"\n  [class.full-height]=\"fullHeight\"\n>\n  <div *ngIf=\"opened\">\n    <div [ngClass]=\"contentCssClasses\">\n      <ng-content></ng-content>\n    </div>\n  </div>\n</div>\n", styles: [":host{display:flex;flex-direction:column;background:var(--cp-base, var(--element-base-1))}:host.base-0{--cp-base: var(--element-base-0);--cp-header: var(--element-base-1)}:host.base-1{--cp-base: var(--element-base-1);--cp-header: var(--element-base-0)}:host:not(:first-of-type){padding-block-start:2px}:host:not(.hcollapsed) .collapsed-icon,:host:not(.hcollapsed) .badge-text{display:none}:host.hcollapsed .collapsible-header .autohide,:host.hcollapsed .collapsible-content{display:none}:host.hcollapsed .collapsible-header{background:var(--element-base-1);padding-block:8px}:host.hcollapsed .collapsible-header:hover{background:var(--element-base-1-hover)}:host.full-height{transition:flex-grow calc(.5s * var(--element-animations-enabled, 1))}:host.full-height.opened,:host.full-height .collapsible-content{flex:1 1 0}:host.full-height .collapsible-content>div{flex:1 0 0}.collapsible-header{display:flex;align-items:center;background:var(--cp-header, var(--element-base-0));color:var(--element-text-primary);padding-block:12px}.collapsible-header.disabled{color:var(--element-text-disabled)}.collapsible-header:not(.disabled){cursor:pointer}.collapsible-header:not(.disabled):hover{text-decoration:none;background:var(--element-base-1-selected)}.dropdown-caret{transition:transform calc(.5s * var(--element-animations-enabled, 1));color:var(--element-ui-1)}.open .dropdown-caret{transform:rotate(180deg)}.collapsible-content{overflow:hidden;display:flex;flex-direction:column}.collapsible-content.full-height{overflow:auto}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: i2.SiTranslatePipe, name: "translate" }], animations: [
            trigger('showHide', [
                transition('*=>hide', [
                    style({ overflow: 'hidden' }),
                    query(':leave', [style({ blockSize: '*' }), animate('0.5s ease', style({ blockSize: '0' }))], { optional: true })
                ]),
                transition('*=>show', [
                    style({ overflow: 'hidden' }),
                    query(':enter', [style({ blockSize: '0' }), animate('0.5s ease', style({ blockSize: '*' }))], { optional: true })
                ])
            ])
        ], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiCollapsiblePanelComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-collapsible-panel', animations: [
                        trigger('showHide', [
                            transition('*=>hide', [
                                style({ overflow: 'hidden' }),
                                query(':leave', [style({ blockSize: '*' }), animate('0.5s ease', style({ blockSize: '0' }))], { optional: true })
                            ]),
                            transition('*=>show', [
                                style({ overflow: 'hidden' }),
                                query(':enter', [style({ blockSize: '0' }), animate('0.5s ease', style({ blockSize: '*' }))], { optional: true })
                            ])
                        ])
                    ], changeDetection: ChangeDetectionStrategy.OnPush, template: "<div\n  class=\"collapsible-header focus-inside px-6\"\n  role=\"button\"\n  [ngClass]=\"headerCssClasses\"\n  [attr.tabindex]=\"disabled ? '' : '0'\"\n  [id]=\"headerId\"\n  [class.open]=\"opened\"\n  [class.disabled]=\"disabled\"\n  [title]=\"hcollapsed ? (heading | translate) : ''\"\n  [attr.aria-expanded]=\"opened && !hcollapsed\"\n  [attr.aria-disabled]=\"disabled\"\n  [attr.aria-controls]=\"controlId\"\n  [attr.aria-label]=\"hcollapsed ? (heading | translate) : ''\"\n  (keydown)=\"keydown($event)\"\n  (click)=\"doToggle($event)\"\n>\n  <i\n    *ngIf=\"icon\"\n    class=\"icon ms-n2 me-2\"\n    aria-hidden=\"true\"\n    [class.collapsed-icon]=\"isHCollapsible\"\n    [ngClass]=\"icon\"\n  ></i>\n  <span *ngIf=\"icon && badge !== undefined && badge !== ''\" class=\"badge-text\">\n    {{ badge }}\n  </span>\n  <span class=\"si-title-2 autohide\">{{ heading | translate }}</span>\n  <span\n    *ngIf=\"badge !== undefined && badge !== ''\"\n    class=\"badge autohide\"\n    [ngClass]=\"'bg-' + badgeColor\"\n  >\n    {{ badge }}\n  </span>\n  <span class=\"ms-auto overflow-hidden\">\n    <span class=\"icon dropdown-caret element-down-2\" aria-hidden=\"true\"></span>\n  </span>\n</div>\n<div\n  #content\n  class=\"collapsible-content\"\n  role=\"region\"\n  [id]=\"controlId\"\n  [ngClass]=\"contentBgClasses\"\n  [@showHide]=\"showHide\"\n  [attr.aria-labelledby]=\"headerId\"\n  [class.full-height]=\"fullHeight\"\n>\n  <div *ngIf=\"opened\">\n    <div [ngClass]=\"contentCssClasses\">\n      <ng-content></ng-content>\n    </div>\n  </div>\n</div>\n", styles: [":host{display:flex;flex-direction:column;background:var(--cp-base, var(--element-base-1))}:host.base-0{--cp-base: var(--element-base-0);--cp-header: var(--element-base-1)}:host.base-1{--cp-base: var(--element-base-1);--cp-header: var(--element-base-0)}:host:not(:first-of-type){padding-block-start:2px}:host:not(.hcollapsed) .collapsed-icon,:host:not(.hcollapsed) .badge-text{display:none}:host.hcollapsed .collapsible-header .autohide,:host.hcollapsed .collapsible-content{display:none}:host.hcollapsed .collapsible-header{background:var(--element-base-1);padding-block:8px}:host.hcollapsed .collapsible-header:hover{background:var(--element-base-1-hover)}:host.full-height{transition:flex-grow calc(.5s * var(--element-animations-enabled, 1))}:host.full-height.opened,:host.full-height .collapsible-content{flex:1 1 0}:host.full-height .collapsible-content>div{flex:1 0 0}.collapsible-header{display:flex;align-items:center;background:var(--cp-header, var(--element-base-0));color:var(--element-text-primary);padding-block:12px}.collapsible-header.disabled{color:var(--element-text-disabled)}.collapsible-header:not(.disabled){cursor:pointer}.collapsible-header:not(.disabled):hover{text-decoration:none;background:var(--element-base-1-selected)}.dropdown-caret{transition:transform calc(.5s * var(--element-animations-enabled, 1));color:var(--element-ui-1)}.open .dropdown-caret{transform:rotate(180deg)}.collapsible-content{overflow:hidden;display:flex;flex-direction:column}.collapsible-content.full-height{overflow:auto}\n"] }]
        }], ctorParameters: function () { return []; }, propDecorators: { heading: [{
                type: Input,
                args: [{ required: true }]
            }], headerCssClasses: [{
                type: Input
            }], contentBgClasses: [{
                type: Input
            }], contentCssClasses: [{
                type: Input
            }], opened: [{
                type: HostBinding,
                args: ['class.opened']
            }, {
                type: Input
            }], icon: [{
                type: Input
            }], disabled: [{
                type: Input
            }], colorVariant: [{
                type: HostBinding,
                args: ['class']
            }, {
                type: Input
            }], badge: [{
                type: Input
            }], badgeColor: [{
                type: Input
            }], toggle: [{
                type: Output
            }], hcollapsed: [{
                type: HostBinding,
                args: ['class.hcollapsed']
            }], fullHeight: [{
                type: HostBinding,
                args: ['class.full-height']
            }], contentRef: [{
                type: ViewChild,
                args: ['content', { static: true }]
            }] } });

const PANEL_MIN_HEIGHT = 100;
class SiAccordionComponent {
    expandFirstPanel = true;
    fullHeight = false;
    hcollapsed = false;
    /** Color to use for component background */
    colorVariant;
    panels;
    responsive = false;
    subscription;
    service = inject(SiAccordionService);
    resizeObserver = inject(ResizeObserverService);
    element = inject(ElementRef);
    cdRef = inject(ChangeDetectorRef);
    accordionHCollapseService = inject(SiAccordionHCollapseService, { optional: true });
    ngOnDestroy() {
        this.subscription?.unsubscribe();
    }
    ngOnChanges() {
        this.service.fullHeight$.next(this.fullHeight && !this.responsive);
    }
    ngAfterContentInit() {
        this.subscription = this.resizeObserver
            .observe(this.element.nativeElement, 100, true, true)
            .subscribe(() => this.calcFullHeight());
        if (this.accordionHCollapseService) {
            this.subscription.add(this.accordionHCollapseService.hcollapsed$.subscribe(state => {
                this.hcollapsed = state;
                this.cdRef.markForCheck();
            }));
        }
        if (this.panels.length) {
            this.panels.first.openClose(this.expandFirstPanel, false);
        }
    }
    calcFullHeight() {
        if (this.panels?.length) {
            const height = this.element.nativeElement.offsetHeight;
            this.responsive = !this.hcollapsed && height < this.panels.length * PANEL_MIN_HEIGHT;
            this.service.fullHeight$.next(this.fullHeight && !this.responsive);
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAccordionComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.1.5", type: SiAccordionComponent, selector: "si-accordion", inputs: { expandFirstPanel: "expandFirstPanel", fullHeight: "fullHeight", hcollapsed: "hcollapsed", colorVariant: "colorVariant" }, host: { properties: { "class.full-height": "this.fullHeight", "class.hcollapsed": "this.hcollapsed", "class": "this.colorVariant" } }, providers: [SiAccordionService], queries: [{ propertyName: "panels", predicate: SiCollapsiblePanelComponent }], usesOnChanges: true, ngImport: i0, template: '<div><ng-content></ng-content></div>', isInline: true, styles: [":host,:host>div{display:flex;flex-direction:column}:host.full-height{block-size:100%}:host.full-height>div{overflow-y:auto;flex:1 1 0}:host.full-height.hcollapsed>div{overflow-y:unset;flex-basis:auto}:host.base-0{--cp-base: var(--element-base-0);--cp-header: var(--element-base-1)}:host.base-1{--cp-base: var(--element-base-1);--cp-header: var(--element-base-0)}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
__decorate([
    WebComponentContentChildren(SiCollapsiblePanelComponent)
], SiAccordionComponent.prototype, "panels", void 0);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAccordionComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-accordion', template: '<div><ng-content></ng-content></div>', providers: [SiAccordionService], changeDetection: ChangeDetectionStrategy.OnPush, styles: [":host,:host>div{display:flex;flex-direction:column}:host.full-height{block-size:100%}:host.full-height>div{overflow-y:auto;flex:1 1 0}:host.full-height.hcollapsed>div{overflow-y:unset;flex-basis:auto}:host.base-0{--cp-base: var(--element-base-0);--cp-header: var(--element-base-1)}:host.base-1{--cp-base: var(--element-base-1);--cp-header: var(--element-base-0)}\n"] }]
        }], propDecorators: { expandFirstPanel: [{
                type: Input
            }], fullHeight: [{
                type: HostBinding,
                args: ['class.full-height']
            }, {
                type: Input
            }], hcollapsed: [{
                type: HostBinding,
                args: ['class.hcollapsed']
            }, {
                type: Input
            }], colorVariant: [{
                type: HostBinding,
                args: ['class']
            }, {
                type: Input
            }], panels: [{
                type: ContentChildren,
                args: [SiCollapsiblePanelComponent]
            }] } });

class SiAccordionModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAccordionModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.1.5", ngImport: i0, type: SiAccordionModule, declarations: [SiAccordionComponent, SiCollapsiblePanelComponent], imports: [CommonModule, SiTranslateModule], exports: [SiAccordionComponent, SiCollapsiblePanelComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAccordionModule, imports: [CommonModule, SiTranslateModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAccordionModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, SiTranslateModule],
                    declarations: [SiAccordionComponent, SiCollapsiblePanelComponent],
                    exports: [SiAccordionComponent, SiCollapsiblePanelComponent]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { SiAccordionComponent, SiAccordionHCollapseService, SiAccordionModule, SiAccordionService, SiCollapsiblePanelComponent };
